import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Lock, User as UserIcon, Mail, Shield, Trash2, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const AdminProfile = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [deletingData, setDeletingData] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Nye passord stemmer ikke overens');
      return;
    }

    if (passwordData.new_password.length < 8) {
      toast.error('Passord må være minst 8 tegn');
      return;
    }

    try {
      setLoading(true);
      await axios.put(`${API}/auth/change-password`, {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Passord endret! Logger ut...');
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
      
      setTimeout(() => {
        logout();
        navigate('/admin/login');
      }, 2000);
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke endre passord');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMyData = async () => {
    try {
      setDeletingData(true);
      await axios.delete(`${API}/auth/delete-my-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Alle dine data har blitt slettet. Logger ut...');
      
      setTimeout(() => {
        logout();
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error deleting data:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke slette data');
    } finally {
      setDeletingData(false);
    }
  };

  return (
    <div className="min-h-screen bg-black p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-white">
            <span className="bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text text-transparent">
              Min Profil
            </span>
          </h1>
          <Button onClick={() => navigate('/admin/dashboard')} variant="outline" className="border-gray-600 text-gray-300">
            Tilbake til Dashboard
          </Button>
        </div>

        {/* User Info Card */}
        <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center space-x-2">
              <UserIcon className="w-6 h-6 text-blue-400" />
              <span>Brukerinformasjon</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-400 text-sm">Navn</Label>
              <p className="text-white text-lg font-semibold">{user?.name}</p>
            </div>
            <div>
              <Label className="text-gray-400 text-sm flex items-center space-x-2">
                <Mail className="w-4 h-4" />
                <span>Email</span>
              </Label>
              <p className="text-white text-lg">{user?.email}</p>
            </div>
            <div>
              <Label className="text-gray-400 text-sm flex items-center space-x-2">
                <Shield className="w-4 h-4" />
                <span>Rolle</span>
              </Label>
              <p className="text-white text-lg capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </CardContent>
        </Card>

        {/* Change Password Card */}
        <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center space-x-2">
              <Lock className="w-6 h-6 text-red-400" />
              <span>Endre Passord</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label className="text-gray-300">Nåværende Passord</Label>
                <Input
                  type="password"
                  value={passwordData.old_password}
                  onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                  className="bg-gray-900 border-gray-700 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-gray-300">Nytt Passord (min. 8 tegn)</Label>
                <Input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="bg-gray-900 border-gray-700 text-white"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <Label className="text-gray-300">Bekreft Nytt Passord</Label>
                <Input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  className="bg-gray-900 border-gray-700 text-white"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600"
              >
                {loading ? 'Endrer...' : 'Endre Passord'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* GDPR Data Deletion Card */}
        <Card className="bg-gradient-to-br from-red-900/30 to-red-800/30 border-red-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center space-x-2">
              <Trash2 className="w-6 h-6 text-red-400" />
              <span>Slett Mine Data (GDPR)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-300">
                  <p className="font-semibold text-white mb-2">⚠️ Advarsel: Dette kan ikke angres!</p>
                  <p>Ved å slette dine data vil følgende skje:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Alle dine bookinger vil bli anonymisert</li>
                    <li>Din brukerkonto vil bli deaktivert</li>
                    <li>Alle logger knyttet til deg vil bli slettet</li>
                    <li>Du vil bli logget ut umiddelbart</li>
                  </ul>
                </div>
              </div>
            </div>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  className="w-full bg-red-600 hover:bg-red-700"
                  disabled={deletingData}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {deletingData ? 'Sletter...' : 'Slett Alle Mine Data'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-gray-900 border-gray-700">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white">Er du helt sikker?</AlertDialogTitle>
                  <AlertDialogDescription className="text-gray-300">
                    Denne handlingen kan ikke angres. Dette vil permanent slette eller anonymisere alle dine personopplysninger fra våre systemer.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-gray-800 text-white border-gray-700">
                    Avbryt
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteMyData}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Ja, slett alle mine data
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminProfile;
